import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { useData } from './DataContext';

export interface Chat {
  id: string;
  name: string;
  created_at: string;
  updated_at: string;
}

interface ChatsContextType {
  chats: Chat[];
  currentChatId: string | null;
  isLoading: boolean;
  handleNewChat: () => void;
  handleChatSelect: (chatId: string) => void;
  handleRenameChat: (chatId: string, newName: string) => Promise<void>;
  handleDeleteChat: (chatId: string) => Promise<void>;
  refreshChats: () => void;
}

interface ChatsProviderProps {
  children: React.ReactNode;
  domainId: string | null;
  customerGroupId: string | null;
}

const ChatsContext = createContext<ChatsContextType | undefined>(undefined);

export const ChatsProvider: React.FC<ChatsProviderProps> = ({
  children,
  domainId,
  customerGroupId,
}) => {
  const [chats, setChats] = useState<Chat[]>([]);
  const [currentChatId, setCurrentChatId] = useState<string | null>(null);
  const { targetCompany } = useData();
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const fetchChats = useCallback(async () => {
    if (!targetCompany || !domainId || !customerGroupId) return;

    setIsLoading(true);
    const { data, error } = await supabase
      .from('chats')
      .select('*')
      .eq('company_id', targetCompany)
      .eq('domain_id', domainId)
      .eq('customer_target_group_id', customerGroupId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching chats:', error);
      return;
    }

    setChats(data || []);
    setIsLoading(false);
  }, [targetCompany, domainId, customerGroupId, refreshTrigger]);

  useEffect(() => {
    fetchChats();
  }, [fetchChats]);

  const handleChatSelect = (chatId: string) => {
    setCurrentChatId(chatId);
  };

  const handleNewChat = () => {
    setCurrentChatId(null);
  };

  const handleRenameChat = async (chatId: string, newName: string) => {
    const { error } = await supabase.from('chats').update({ name: newName }).eq('id', chatId);

    if (error) {
      console.error('Error renaming chat:', error);
      return;
    }

    setRefreshTrigger((prev) => prev + 1);
  };

  const handleDeleteChat = async (chatId: string) => {
    setIsLoading(true);
    const { error } = await supabase.from('chats').delete().eq('id', chatId);

    if (error) {
      console.error('Error deleting chat:', error);
      setIsLoading(false);
      return;
    }
    if (currentChatId === chatId) {
      setCurrentChatId(null);
    }
    setRefreshTrigger((prev) => prev + 1);
  };

  const refreshChats = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  const value = {
    chats,
    currentChatId,
    isLoading,
    handleNewChat,
    handleChatSelect,
    handleRenameChat,
    handleDeleteChat,
    refreshChats,
  };

  return <ChatsContext.Provider value={value}>{children}</ChatsContext.Provider>;
};

export const useChats = () => {
  const context = useContext(ChatsContext);
  if (context === undefined) {
    throw new Error('useChats must be used within a ChatsProvider');
  }
  return context;
};
